import {
  OpticalJobStatus,
  PERMISSIONS,
  PaymentTransactionType,
  SalesOrderStatus,
  type OpticalJobDetail,
  type OpticalJobStatus as JobStatus,
  type PaymentMethod,
  type PaymentTransactionSummary,
  type SalesOrderDetail,
} from '@optiflow/types';
import { methodNeedsReference } from '@/modules/sales/tenderMethods';
import type { OpticalJobAction } from '../api/opticalJobs.api';

/** The six steps a job moves through, in order. CANCELLED stands outside them: it is where a whole order went before the lab. */
export const JOB_STEPS: readonly JobStatus[] = [
  OpticalJobStatus.ORDERED,
  OpticalJobStatus.SENT_TO_LAB,
  OpticalJobStatus.IN_LAB,
  OpticalJobStatus.RECEIVED_FROM_LAB,
  OpticalJobStatus.READY_FOR_COLLECTION,
  OpticalJobStatus.DELIVERED,
];

export interface NextStep {
  action: OpticalJobAction;
  to: JobStatus;
  permission: string;
}

/** The one step forward each status allows, and the permission it needs. There is no step back. */
export const NEXT_STEP: Partial<Record<JobStatus, NextStep>> = {
  ORDERED: {
    action: 'send-to-lab',
    to: OpticalJobStatus.SENT_TO_LAB,
    permission: PERMISSIONS.OPTICAL_LAB_WORKFLOW,
  },
  SENT_TO_LAB: {
    action: 'mark-in-lab',
    to: OpticalJobStatus.IN_LAB,
    permission: PERMISSIONS.OPTICAL_LAB_WORKFLOW,
  },
  IN_LAB: {
    action: 'receive-from-lab',
    to: OpticalJobStatus.RECEIVED_FROM_LAB,
    permission: PERMISSIONS.OPTICAL_LAB_WORKFLOW,
  },
  RECEIVED_FROM_LAB: {
    action: 'mark-ready',
    to: OpticalJobStatus.READY_FOR_COLLECTION,
    permission: PERMISSIONS.OPTICAL_LAB_WORKFLOW,
  },
  READY_FOR_COLLECTION: {
    action: 'deliver',
    to: OpticalJobStatus.DELIVERED,
    permission: PERMISSIONS.OPTICAL_DELIVER,
  },
};

export type StatusTone = 'neutral' | 'brand' | 'success' | 'warning' | 'danger';

/** The colour a status is shown in, always beside its name: ready stands out, cancelled reads as stopped. */
export function statusTone(status: JobStatus): StatusTone {
  switch (status) {
    case OpticalJobStatus.READY_FOR_COLLECTION:
      return 'success';
    case OpticalJobStatus.CANCELLED:
      return 'danger';
    case OpticalJobStatus.DELIVERED:
      return 'neutral';
    case OpticalJobStatus.ORDERED:
      return 'warning';
    default:
      return 'brand';
  }
}

/** One payment the cancellation would return: what is left of it after the refunds that name it. */
export interface RefundablePayment {
  paymentTransactionId: string;
  method: PaymentMethod;
  amountMinor: number;
  needsReference: boolean;
}

/**
 * What a cancellation would refund, payment by payment, oldest first — the server's own reckoning, shown so the right references can be asked for before anything is sent.
 *
 * A reference is asked for only where the tender carries one; never for cash.
 */
export function refundablePayments(
  payments: readonly PaymentTransactionSummary[],
): RefundablePayment[] {
  const refunded = new Map<string, number>();
  for (const row of payments) {
    if (row.type === PaymentTransactionType.REFUND && row.originalPaymentTransactionId) {
      refunded.set(
        row.originalPaymentTransactionId,
        (refunded.get(row.originalPaymentTransactionId) ?? 0) + row.amountMinor,
      );
    }
  }
  return payments
    .filter((row) => row.type === PaymentTransactionType.PAYMENT)
    .slice()
    .sort((a, b) => a.occurredAt.localeCompare(b.occurredAt))
    .map((row) => ({
      paymentTransactionId: row.id,
      method: row.method,
      amountMinor: row.amountMinor - (refunded.get(row.id) ?? 0),
      needsReference: methodNeedsReference(row.method),
    }))
    .filter((refund) => refund.amountMinor > 0);
}

/** Why an order cannot be cancelled here, or null when it may be offered. The server decides; this only keeps the action off orders it would refuse. */
export type CancellationBlock = 'afterLab' | 'mixed' | 'notConfirmed' | 'handedOver' | 'noJobs';

export function cancellationBlock(
  order: Pick<SalesOrderDetail, 'status' | 'lines'>,
  jobs: readonly Pick<OpticalJobDetail, 'status'>[],
): CancellationBlock | null {
  if (jobs.length === 0) return 'noJobs';
  if (jobs.some((job) => job.status !== OpticalJobStatus.ORDERED)) return 'afterLab';
  if (order.status !== SalesOrderStatus.CONFIRMED) return 'notConfirmed';
  if (order.lines.some((line) => line.opticalJobId === null)) return 'mixed';
  if (order.lines.some((line) => line.quantityDelivered > 0)) return 'handedOver';
  return null;
}
